/**
 * Initialize zoomable image functionality
 * Uses DOMContentLoaded instead of window.load for faster initialization
 * Images are loaded on-demand when zoom is activated, so full page load isn't required
 */
document.addEventListener('DOMContentLoaded', function () {
  const zoomableContainer = document.getElementById('zoomable-container');
  const zoomableContent = document.getElementById('zoomable-content');
  const zoomableImages = Array.from(document.querySelectorAll('[data-zoomable]'));

  let currentZoomLevel = 0; // 0: closed, 1: first zoom, 2: second zoom with pan
  let isPanning = false;
  let panStartX = 0;
  let panStartY = 0;
  let currentPanX = 0;
  let currentPanY = 0;
  let zoomedImage = null;
  let animationFrameId = null;

  zoomableContent.innerHTML = '';

  // Function to handle image zooming and panning
  function setupZoomedImage(img) {
    zoomedImage = img;
    currentZoomLevel = 1;
    currentPanX = 0;
    currentPanY = 0;
    
    // Add zoom and pan event listeners to the zoomed image
    let startTime;
    let startX;
    let startY;
    
    img.addEventListener('mousedown', function(e) {
      e.preventDefault();
      e.stopPropagation();
      startTime = new Date().getTime();
      startX = e.pageX;
      startY = e.pageY;
      
      if (currentZoomLevel === 2) {
        isPanning = true;
        panStartX = e.pageX - currentPanX;
        panStartY = e.pageY - currentPanY;
        img.style.cursor = 'grabbing';
      }
    });

    img.addEventListener('touchstart', function(e) {
      e.preventDefault();
      e.stopPropagation();
      startTime = new Date().getTime();
      startX = e.touches[0].pageX;
      startY = e.touches[0].pageY;
      
      if (currentZoomLevel === 2) {
        isPanning = true;
        panStartX = e.touches[0].pageX - currentPanX;
        panStartY = e.touches[0].pageY - currentPanY;
      }
    });

    img.addEventListener('mouseup', function(e) {
      e.stopPropagation();
      const endTime = new Date().getTime();
      const endX = e.pageX;
      const endY = e.pageY;
      handleZoomedImageRelease(endTime, endX, endY);
      isPanning = false;
      img.style.cursor = currentZoomLevel === 2 ? 'grab' : 'zoom-in';
      
      // Cancel any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    });

    img.addEventListener('touchend', function(e) {
      e.stopPropagation();
      const endTime = new Date().getTime();
      const endX = e.changedTouches[0].pageX;
      const endY = e.changedTouches[0].pageY;
      handleZoomedImageRelease(endTime, endX, endY);
      isPanning = false;
      
      // Cancel any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    });

    img.addEventListener('mousemove', function(e) {
      if (isPanning && currentZoomLevel === 2) {
        e.preventDefault();
        currentPanX = e.pageX - panStartX;
        currentPanY = e.pageY - panStartY;
        
        // Use requestAnimationFrame for smooth 60fps updates
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(updateImageTransform);
      }
    });

    img.addEventListener('touchmove', function(e) {
      if (isPanning && currentZoomLevel === 2) {
        e.preventDefault();
        currentPanX = e.touches[0].pageX - panStartX;
        currentPanY = e.touches[0].pageY - panStartY;
        
        // Use requestAnimationFrame for smooth 60fps updates
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(updateImageTransform);
      }
    });

    function handleZoomedImageRelease(endTime, endX, endY) {
      const timeDiff = endTime - startTime;
      const moveDistance = Math.sqrt(
        Math.pow(endX - startX, 2) + 
        Math.pow(endY - startY, 2)
      );

      // If it was a quick tap/click without much movement
      if (timeDiff < 200 && moveDistance < 10) {
        if (currentZoomLevel === 1) {
          // Go to second zoom level with animation
          currentZoomLevel = 2;
          zoomableContainer.classList.add('second-zoom');
          img.style.cursor = 'grab';
          updateImageTransform(true); // animated = true
        } else if (currentZoomLevel === 2) {
          // Go back to first zoom level with smooth zoom-out animation
          currentZoomLevel = 1;
          zoomableContainer.classList.remove('second-zoom');
          img.style.cursor = 'zoom-in';
          
          // Animate zoom-out with pan reset
          animateZoomOut();
        }
      }
    }

    function updateImageTransform(animated = false) {
      // Handle transitions based on context
      if (isPanning) {
        // No transition during panning for smooth movement
        img.style.transition = 'none';
      } else if (animated) {
        // Smooth transition for zoom level changes
        img.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      } else {
        // Default transition
        img.style.transition = 'transform 0.3s ease';
      }
      
      if (currentZoomLevel === 2) {
        // Use translate3d for hardware acceleration
        img.style.transform = `scale(2.5) translate3d(${currentPanX / 2.5}px, ${currentPanY / 2.5}px, 0)`;
      } else {
        img.style.transform = 'scale(1) translate3d(0px, 0px, 0)';
      }
    }

    function animateZoomOut() {
      // Enhanced two-stage zoom-out animation with visual feedback
      zoomableContainer.classList.add('zooming-out');
      
      // Stage 1: Smoothly pan back to center while zoomed
      if (currentPanX !== 0 || currentPanY !== 0) {
        img.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        img.style.transform = `scale(2.5) translate3d(0px, 0px, 0)`;
        
        // Stage 2: After centering, zoom out with elegant ease
        setTimeout(() => {
          img.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
          img.style.transform = 'scale(1) translate3d(0px, 0px, 0)';
          currentPanX = 0;
          currentPanY = 0;
          
          // Remove zoom-out class after animation completes
          setTimeout(() => {
            zoomableContainer.classList.remove('zooming-out');
          }, 400);
        }, 300);
      } else {
        // If already centered, just zoom out directly
        img.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
        img.style.transform = 'scale(1) translate3d(0px, 0px, 0)';
        currentPanX = 0;
        currentPanY = 0;
        
        // Remove zoom-out class after animation completes
        setTimeout(() => {
          zoomableContainer.classList.remove('zooming-out');
        }, 400);
      }
    }

    // Prevent native browser image dragging
    img.addEventListener('dragstart', function(e) {
      e.preventDefault();
      return false;
    });
    
    img.addEventListener('selectstart', function(e) {
      e.preventDefault();
      return false;
    });
    
    img.addEventListener('contextmenu', function(e) {
      e.preventDefault();
      return false;
    });
    
    // Set initial cursor
    img.style.cursor = 'zoom-in';
    img.style.transition = 'transform 0.3s ease';
  }

  zoomableImages.forEach(image => {
    let startTime;
    let startX;
    let startY;
    
    image.addEventListener('mousedown', function(e) {
      startTime = new Date().getTime();
      startX = e.pageX;
      startY = e.pageY;
    });

    image.addEventListener('touchstart', function(e) {
      startTime = new Date().getTime();
      startX = e.touches[0].pageX;
      startY = e.touches[0].pageY;
    });

    image.addEventListener('mouseup', function(e) {
      const endTime = new Date().getTime();
      const endX = e.pageX;
      const endY = e.pageY;
      handleRelease(endTime, endX, endY);
    });

    image.addEventListener('touchend', function(e) {
      const endTime = new Date().getTime();
      const endX = e.changedTouches[0].pageX;
      const endY = e.changedTouches[0].pageY;
      handleRelease(endTime, endX, endY);
    });

    function handleRelease(endTime, endX, endY) {
      const timeDiff = endTime - startTime;
      const moveDistance = Math.sqrt(
        Math.pow(endX - startX, 2) + 
        Math.pow(endY - startY, 2)
      );

      // If interaction was less than 200ms and movement was less than 10px
      if (timeDiff < 200 && moveDistance < 10) {
        zoomableContainer.classList.add('active');
        zoomableContainer.classList.remove('second-zoom');
        const img = new Image();
        img.src = image.dataset.zoomable;
        
        // Disable native browser image dragging
        img.draggable = false;
        img.setAttribute('draggable', 'false');
        
        zoomableContent.innerHTML = '';
        zoomableContent.appendChild(img);
        
        // Add entrance animation
        img.style.transform = 'scale(0.8) translate3d(0px, 0px, 0)';
        img.style.opacity = '0';
        
        // Trigger entrance animation after image loads
        img.onload = function() {
          img.style.transition = 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease';
          setTimeout(() => {
            img.style.transform = 'scale(1) translate3d(0px, 0px, 0)';
            img.style.opacity = '1';
          }, 50);
        };
        
        setupZoomedImage(img);
      }
    }
  });

  function closeZoom() {
    if (zoomedImage && currentZoomLevel > 0) {
      // Add exit animation
      zoomedImage.style.transition = 'transform 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19), opacity 0.3s ease';
      zoomedImage.style.transform = 'scale(0.8) translate3d(0px, 0px, 0)';
      zoomedImage.style.opacity = '0';
      
      setTimeout(() => {
        zoomableContainer.classList.remove('active');
        zoomableContainer.classList.remove('second-zoom');
        zoomableContent.innerHTML = '';
        currentZoomLevel = 0;
        zoomedImage = null;
      }, 300);
    } else {
      // Fallback if no animation needed
      zoomableContainer.classList.remove('active');
      zoomableContainer.classList.remove('second-zoom');
      zoomableContent.innerHTML = '';
      currentZoomLevel = 0;
      zoomedImage = null;
    }
  }

  zoomableContainer.addEventListener('click', function (e) {
    // Only close if clicking the background, not the image
    if (e.target === zoomableContainer || e.target === zoomableContent) {
      closeZoom();
    }
  });

  // Add click handler for close button
  const closeButton = zoomableContainer.querySelector('.close-button-container svg');
  if (closeButton) {
    closeButton.addEventListener('click', function(e) {
      e.stopPropagation();
      closeZoom();
    });
  }
});